<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Stale Flags
                            </h2>
                        </div>
                    </div>
                    <!-- Denied state -->
                    <zen-denied-state
                        v-if="!canView"
                        title="Stale flags restricted"
                        message="Stale flags need a higher plan — no sales call, just upgrade in Billing."
                    />

                    <template v-if="canView">
                        <project-change-notice
                            :project-key="current.projectKey"
                            @refresh="load"
                        />
                        <div
                            class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                        >
                            <div>
                                <label
                                    for="stale-flags-filter-threshold"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Not evaluated in
                                </label>
                                <select
                                    id="stale-flags-filter-threshold"
                                    v-model.number="filters.staleAfterDays"
                                    data-testid="stale-flags-filter-threshold"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option :value="30">30+ days</option>
                                    <option :value="60">60+ days</option>
                                    <option :value="90">90+ days</option>
                                </select>
                            </div>
                            <div>
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    data-testid="stale-flags-apply-filters"
                                    @click="applyFilters"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <zen-message-block v-if="error" type="error">
                            {{
                                error.message ||
                                'Failed to load stale flags. Please try again.'
                            }}
                        </zen-message-block>

                        <template v-if="!loading && !error">
                            <div
                                class="flex flex-wrap items-center justify-between gap-3"
                            >
                                <p
                                    class="text-sm text-taupe-600"
                                    data-testid="stale-flags-summary"
                                >
                                    <span class="font-medium text-taupe-800">{{
                                        staleCount
                                    }}</span>
                                    of {{ totalFlags }} flags haven't been
                                    evaluated in {{ filters.staleAfterDays }}+
                                    days.
                                </p>
                                <zen-button
                                    v-if="
                                        can(
                                            PERMISSIONS.FLAG_DELETE,
                                            permissionScope
                                        )
                                    "
                                    variant="primary-danger"
                                    size="small"
                                    :disabled="selected.length === 0"
                                    data-testid="stale-flags-archive-selected"
                                    @click="openArchiveModal"
                                >
                                    Archive Selected ({{ selected.length }})
                                </zen-button>
                            </div>

                            <div
                                class="-mx-4 mt-2 overflow-hidden shadow-sm sm:-mx-6 md:mx-0 md:rounded-lg"
                            >
                                <table
                                    class="min-w-full divide-y divide-taupe-300"
                                >
                                    <thead class="bg-taupe-50">
                                        <tr>
                                            <th
                                                v-if="
                                                    can(
                                                        PERMISSIONS.FLAG_DELETE,
                                                        permissionScope
                                                    )
                                                "
                                                scope="col"
                                                class="w-10 py-3.5 pl-4 pr-3 sm:pl-6"
                                            >
                                                <input
                                                    type="checkbox"
                                                    aria-label="Select all stale flags"
                                                    data-testid="stale-flags-select-all"
                                                    :checked="allSelected"
                                                    @change="toggleSelectAll"
                                                    class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                                                />
                                            </th>
                                            <th
                                                scope="col"
                                                class="w-full lg:w-2/5 py-3.5 pl-4 pr-3 text-left text-sm lg:text-base font-medium text-taupe-800 sm:pl-6"
                                            >
                                                Flag
                                            </th>
                                            <th
                                                scope="col"
                                                class="w-1/6 hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                Owner
                                            </th>
                                            <th
                                                scope="col"
                                                class="w-1/6 hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                Age
                                            </th>
                                            <th
                                                scope="col"
                                                class="w-1/6 hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                Last Evaluated
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        class="divide-y divide-taupe-200 bg-white"
                                    >
                                        <tr
                                            v-for="flag in flags"
                                            :key="flag.key"
                                        >
                                            <td
                                                v-if="
                                                    can(
                                                        PERMISSIONS.FLAG_DELETE,
                                                        permissionScope
                                                    )
                                                "
                                                class="py-4 pl-4 pr-3 sm:pl-6"
                                            >
                                                <input
                                                    type="checkbox"
                                                    :aria-label="`Select ${flag.name}`"
                                                    :value="flag.key"
                                                    v-model="selected"
                                                    class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                                                />
                                            </td>
                                            <td
                                                class="w-full max-w-0 py-4 pl-4 pr-3 text-sm lg:text-base font-medium text-taupe-800 sm:w-auto sm:max-w-none sm:pl-6"
                                            >
                                                <div>
                                                    {{ flag.name }}
                                                </div>
                                                <div
                                                    class="font-light font-mono text-sm text-taupe-800"
                                                >
                                                    <router-link
                                                        v-if="flag.key"
                                                        :to="{
                                                            name: 'ProjectFlagDetailScreen',
                                                            params: {
                                                                projectKey:
                                                                    current.projectKey,
                                                                environmentKey:
                                                                    current.environmentKey,
                                                                key: flag.key,
                                                            },
                                                        }"
                                                        class="flex space-x-1 text-taupe-500 hover:text-sky-500"
                                                    >
                                                        <span>{{
                                                            flag.key
                                                        }}</span>
                                                        <zen-icon-open-link
                                                            css-classes="h-4 w-4"
                                                            stroke-width="2"
                                                        />
                                                    </router-link>
                                                    <span v-else>—</span>
                                                </div>
                                                <dl
                                                    class="font-light lg:hidden"
                                                >
                                                    <dt class="sr-only">
                                                        Owner
                                                    </dt>
                                                    <dd
                                                        class="mt-1 truncate text-taupe-800"
                                                    >
                                                        {{
                                                            flag.owner?.name ??
                                                            'Unassigned'
                                                        }}
                                                    </dd>
                                                    <dt class="sr-only">Age</dt>
                                                    <dd
                                                        class="mt-1 truncate text-taupe-800"
                                                    >
                                                        {{ flag.age_days }} days
                                                    </dd>
                                                    <dt class="sr-only">
                                                        Last Evaluated
                                                    </dt>
                                                    <dd
                                                        class="mt-1 truncate text-taupe-800"
                                                    >
                                                        <span
                                                            v-if="
                                                                flag.never_evaluated
                                                            "
                                                            >Never</span
                                                        >
                                                        <zen-date-time
                                                            v-else
                                                            human
                                                            :value="
                                                                flag.last_evaluated_at
                                                            "
                                                        />
                                                    </dd>
                                                </dl>
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                {{
                                                    flag.owner?.name ??
                                                    'Unassigned'
                                                }}
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                {{ flag.age_days }} days
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                <span
                                                    v-if="flag.never_evaluated"
                                                    >Never</span
                                                >
                                                <template v-else>
                                                    <zen-date-time
                                                        human
                                                        :value="
                                                            flag.last_evaluated_at
                                                        "
                                                    />
                                                    <div
                                                        class="text-xs lg:text-sm"
                                                    >
                                                        <zen-date-time
                                                            :value="
                                                                flag.last_evaluated_at
                                                            "
                                                        />
                                                    </div>
                                                </template>
                                            </td>
                                        </tr>
                                        <tr v-if="flags.length === 0">
                                            <td
                                                :colspan="
                                                    can(
                                                        PERMISSIONS.FLAG_DELETE,
                                                        permissionScope
                                                    )
                                                        ? 5
                                                        : 4
                                                "
                                            >
                                                <zen-empty-table-message
                                                    compact
                                                    icon="ZenIconClock"
                                                >
                                                    No stale flags — nice and
                                                    tidy.
                                                </zen-empty-table-message>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>

                        <zen-pagination
                            v-if="!loading && !error"
                            :page="pagination.page"
                            :total-pages="pagination.total_pages"
                            :total="pagination.total"
                            @change="changePage"
                        />

                        <div v-if="!loading" class="text-sm pb-10">
                            Report Generated:
                            <zen-date-time :value="generatedAt" />
                        </div>
                    </template>
                </div>
            </div>
        </section>

        <archive-stale-flags-modal
            :is-open="modals.archive.show"
            :count="selected.length"
            :processing="modals.archive.processing"
            @close="closeArchiveModal"
            @confirm="confirmArchive"
        />
    </main>
</template>

<script>
import ReportService from '@/services/report.service'
import FlagService from '@/services/flag.service'
import { useSessionStore } from '@/stores/session'
import ArchiveStaleFlagsModal from '@/screens/trusted/project/reports/modals/ArchiveStaleFlagsModal.vue'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { usePlan } from '@/composables/usePlan'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { useServerPagination } from '@/composables/useServerPagination'
import { errorMessage, successMessage } from '@/utils/notifications'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

export default {
    name: 'StaleFlags',
    components: { ArchiveStaleFlagsModal, ProjectChangeNotice },
    setup() {
        const { current } = useCurrentEnvironment()
        const sessionStore = useSessionStore()
        const { can } = usePermissions()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()
        const { pagination, setPage, resetPage, applyResponse, resetOnError } =
            useServerPagination({ perPage: 20 })
        return {
            current,
            sessionStore,
            can,
            PERMISSIONS,
            hasPlanFeature,
            loading,
            error,
            run,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            resetOnError,
        }
    },
    data() {
        return {
            flags: [],
            staleCount: 0,
            totalFlags: 0,
            generatedAt: null,
            selected: [],
            filters: {
                staleAfterDays: 30,
            },
            modals: {
                archive: {
                    show: false,
                    processing: false,
                },
            },
        }
    },
    computed: {
        canView() {
            return this.hasPlanFeature('flag_lifecycle_management')
        },
        allSelected() {
            return (
                this.flags.length > 0 &&
                this.selected.length === this.flags.length
            )
        },
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        permissionScope() {
            return {
                projectUlid: this.current.projectUlid,
                environmentUlid: this.current.environmentUlid,
            }
        },
    },
    watch: {
        environmentScopeKey() {
            this.applyFilters()
        },
    },
    methods: {
        applyFilters() {
            this.resetPage()
            this.load()
        },
        changePage(page) {
            this.setPage(page)
            this.load()
        },
        load() {
            if (!this.canView) return

            this.selected = []

            this.run(
                (signal) =>
                    ReportService.getStaleFlags(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            staleAfterDays: this.filters.staleAfterDays,
                            page: this.pagination.page,
                            perPage: this.pagination.per_page,
                        },
                        { signal }
                    ),
                (response) => {
                    this.flags = response.data.flags
                    this.staleCount = response.data.stale_count
                    this.totalFlags = response.data.total_flags
                    this.generatedAt = response.data.generated_at
                    if (this.applyResponse(response.data.pagination)) {
                        this.load()
                    }
                },
                () => {
                    this.flags = []
                    this.staleCount = 0
                    this.totalFlags = 0
                    this.resetOnError()
                }
            )
        },
        toggleSelectAll(event) {
            this.selected = event.target.checked
                ? this.flags.map((flag) => flag.key)
                : []
        },
        openArchiveModal() {
            this.modals.archive.show = true
        },
        closeArchiveModal() {
            this.modals.archive.show = false
        },
        confirmArchive() {
            this.modals.archive.processing = true

            FlagService.bulkRemove(this.current.projectKey, this.selected)
                .then((response) => {
                    // Keep the flag list in the session store in step with
                    // the archive. The report itself reloads below.
                    response.data.deleted.forEach((key) =>
                        this.sessionStore.removeFlag(key)
                    )
                    successMessage('The selected flags have been archived.')
                    this.closeArchiveModal()
                    this.load()
                })
                .catch((error) => {
                    errorMessage(error)
                })
                .finally(() => {
                    this.modals.archive.processing = false
                })
        },
    },
    mounted() {
        this.load()
    },
}
</script>
